import { useState } from "react";

import toast from "components/partials/toast/toast";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { getTouchpoint } from "generated/api/touchpoint";
import { useSetTouchpointDetailCache } from "hooks/api/use-touchpoints";

import { getErrorMessage } from "utilities";
import { LoadingButtonState } from "ui/button";
import { EditProgramTemplateNameFormData } from "components/modals/edit-program-template-name-modal/edit-program-template-name-modal.types";

interface UseEditProgramTemplateNameProps {
  currentName: string;
  setIsOpen: (isOpen: boolean) => void;
  reset: (values?: { name: string }) => void;
}

export const useEditProgramTemplateName = ({
  currentName,
  setIsOpen,
  reset,
}: UseEditProgramTemplateNameProps) => {
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const setTouchpointDetailCache = useSetTouchpointDetailCache();

  const [submitState, setSubmitState] = useState<LoadingButtonState>(LoadingButtonState.Default);

  const handleSubmit = async (data: EditProgramTemplateNameFormData) => {
    setSubmitState(LoadingButtonState.Loading);

    try {
      const touchpointApi = getTouchpoint();
      const response = await touchpointApi.partiallyUpdateTouchpoint(
        {
          clientId: currentClient.id,
          campaignId: currentCampaign.id,
          id: currentTouchpoint.id,
        },
        { name: data.name },
      );

      setTouchpointDetailCache(
        {
          clientId: currentClient.id,
          campaignId: currentCampaign.id,
        },
        { ...currentTouchpoint, name: response.name },
      );

      setSubmitState(LoadingButtonState.Success);

      toast.success({
        data: {
          title: `Updated template name to "${data.name}"`,
        },
      });

      reset({ name: data.name });
      setSubmitState(LoadingButtonState.Default);
      setIsOpen(false);
    } catch (err: unknown) {
      const error = err as { response?: { data?: unknown } };
      toast.error({
        data: {
          title: "Failed to update template name",
          message: getErrorMessage(error?.response?.data),
        },
      });
      setSubmitState(LoadingButtonState.Default);
    }
  };

  const handleCancelClick = () => {
    reset({ name: currentName });
    setSubmitState(LoadingButtonState.Default);
    setIsOpen(false);
  };

  return {
    submitState,
    handleSubmit,
    handleCancelClick,
  };
};
